import { UNDEFINED_VALUE } from 'const/formatting'
import { DateAggregation, formatDate } from 'helpers/dates'
import {
    formatCerebroDate,
    formatCerebroDateTime,
    formatCurrency,
    formatNumber,
    titleCase,
} from 'helpers/formatting'
import { shortNameToNode } from 'helpers/typeGuard'
import { isUnset } from 'helpers/utilities'
import { Field } from 'types'

import { FieldCreatorSetupOptions } from './localTypes'

// Attaches a hardcoded group-by metadata shim to a field so it shows up as a
// selectable group by in dashboards. Use for fields whose group by is an
// alias on the backend (i.e. not declared as its own metadata entry) or whose
// underlying data source gets swapped at request time.
export function withGroupByPlaceholder<RecordType>(
    field: Field<RecordType>,
    config: {
        id: string
        slug: string
        displayName: string
        shortName?: string
        definition: string
        supportsTotal?: boolean
    }
): Field<RecordType> {
    return {
        ...field,
        id: config.id,
        groupBy: {
            id: config.id,
            metadata_key: config.id,
            slug: config.slug,
            metadata_all_names: [config.id],
            metadata_display_name: config.displayName,
            metadata_format: 'string',
            metadata_is_deprecated: false,
            metadata_release_date: null,
            metadata_end_of_life_date: null,
            metadata_supports_total: config.supportsTotal ?? false,
            display_name: config.displayName,
            short_name: config.shortName ?? config.displayName,
            definition: config.definition,
            is_visible: true,
        },
    }
}

export function createField<RecordType>(
    options: FieldCreatorSetupOptions<RecordType>
): Field<RecordType> {
    return {
        ...options,
        shortName: options.shortName ?? options.name,
        alternateId: options.alternateId ?? options.id,
        isResizeable: options.isResizeable ?? true,
        minWidth: options.minWidth ?? 50,
        isVisible: options.isVisible ?? true,
        renderOptions: {
            ...options.renderOptions,
            render: options.renderOptions?.render
                ? options.renderOptions.render
                : ({ value, record }) =>
                      value ??
                      (typeof options.emptyValue === 'function'
                          ? options.emptyValue(record)
                          : options.emptyValue) ??
                      UNDEFINED_VALUE,
        },
        localDefinition: options.localDefinition ?? '',
        antTableColumnOptions: {
            title:
                options.columnTitle ??
                shortNameToNode(options.shortName) ??
                options.name,
            dataIndex: options.dataIndex,
            width: options.width ?? options.minWidth ?? 100,
            sorter: options.sorter ?? true,
            fixed: options.fixed ?? false,
            align: options.align ?? 'left',
            readonly: options.readonly ?? false,
        },
    }
}

export function createDateField<RecordType>(
    options: FieldCreatorSetupOptions<RecordType> & {
        dateFormat?: string
        dateAggregation?: DateAggregation | null
    }
): Field<RecordType> {
    return createField({
        minWidth: options.minWidth ?? 80,
        width: options.width ?? 110,
        ...options,
        renderOptions: {
            ...options.renderOptions,
            render: options.renderOptions?.render
                ? options.renderOptions.render
                : ({ value, record }) =>
                      isUnset(value)
                          ? ((typeof options.emptyValue === 'function'
                                ? options.emptyValue(record)
                                : options.emptyValue) ?? UNDEFINED_VALUE)
                          : formatDate(
                                value,
                                options.dateAggregation ?? null,
                                options.dateFormat
                            ),
        },
    })
}

export function createCerebroDateField<RecordType>(
    options: FieldCreatorSetupOptions<RecordType>
): Field<RecordType> {
    return createField({
        minWidth: options.minWidth ?? 80,
        width: options.width ?? 110,
        ...options,
        renderOptions: {
            ...options.renderOptions,
            render: options.renderOptions?.render
                ? options.renderOptions.render
                : ({ value }) => <span>{formatCerebroDate(value)}</span>,
        },
    })
}

export function createCerebroDateTimeField<RecordType>(
    options: FieldCreatorSetupOptions<RecordType>
): Field<RecordType> {
    return createField({
        minWidth: options.minWidth ?? 150,
        width: options.width ?? 180,
        ...options,
        renderOptions: {
            ...options.renderOptions,
            render: options.renderOptions?.render
                ? options.renderOptions.render
                : ({ value }) => <span>{formatCerebroDateTime(value)}</span>,
        },
    })
}

export function createNumberField<RecordType>(
    options: FieldCreatorSetupOptions<RecordType> & {
        numberFormat?: string
        showUnsetAsNumber?: boolean
    }
): Field<RecordType> {
    return createField({
        minWidth: options.minWidth ?? 100,
        width: options.width ?? 120,
        align: 'right',
        ...options,
        renderOptions: {
            ...options.renderOptions,
            render: options.renderOptions?.render
                ? options.renderOptions.render
                : ({ value }) => (
                      <span>
                          {formatNumber(
                              value,
                              options.numberFormat ?? '0,0',
                              options.showUnsetAsNumber
                          )}
                      </span>
                  ),
        },
    })
}

function createPercentageField<RecordType>(
    options: FieldCreatorSetupOptions<RecordType> & {
        numberFormat?: string
        showUnsetAsNumber?: boolean
    }
): Field<RecordType> {
    return createField({
        align: 'right',
        ...options,
        renderOptions: {
            ...options.renderOptions,
            render: options.renderOptions?.render
                ? options.renderOptions.render
                : ({ value }) => (
                      <span>
                          {formatNumber(
                              value,
                              options.numberFormat ?? '0,0',
                              options.showUnsetAsNumber
                          )}
                          %
                      </span>
                  ),
        },
    })
}

export function createCurrencyField<RecordType>(
    options: FieldCreatorSetupOptions<RecordType> & { currencyCode?: string }
): Field<RecordType> {
    return createField({
        minWidth: options.minWidth ?? 100,
        width: options.width ?? 120,
        align: 'right',
        ...options,
        renderOptions: {
            ...options.renderOptions,
            render: options.renderOptions?.render
                ? options.renderOptions.render
                : ({ value, record }) => {
                      const currencyCode =
                          (record as any)?.currency_code ??
                          options.currencyCode ??
                          ''
                      const emptyValue =
                          typeof options.emptyValue === 'function'
                              ? options.emptyValue(record)
                              : (options.emptyValue ?? UNDEFINED_VALUE)
                      return (
                          <span>
                              {isUnset(value)
                                  ? emptyValue
                                  : formatCurrency(Number(value), {
                                        decimal: true,
                                        currencyCode,
                                    })}
                          </span>
                      )
                  },
        },
    })
}

export function createTitleField<RecordType>(
    options: FieldCreatorSetupOptions<RecordType>
): Field<RecordType> {
    return createField({
        minWidth: options.minWidth ?? 120,
        width: options.width ?? 150,
        ...options,
        renderOptions: {
            ...options.renderOptions,
            render: options.renderOptions?.render
                ? options.renderOptions.render
                : ({ value, record }) => {
                      if (isUnset(value)) {
                          if (options.emptyValue) {
                              if (typeof options.emptyValue === 'function') {
                                  return options.emptyValue(record)
                              } else {
                                  return options.emptyValue
                              }
                          }
                          return UNDEFINED_VALUE
                      }
                      return titleCase(value)
                  },
        },
    })
}

export function createActionField<RecordType>(
    options: Omit<FieldCreatorSetupOptions<RecordType>, 'name' | 'shortName'>
): Field<RecordType> {
    return createField({
        ...options,
        name: '',
        shortName: '',
        renderOptions: {
            ...options.renderOptions,
            render: options.renderOptions?.render
                ? options.renderOptions.render
                : ({ value, record }) => {
                      if (isUnset(value)) {
                          if (options.emptyValue) {
                              if (typeof options.emptyValue === 'function') {
                                  return options.emptyValue(record)
                              } else {
                                  return options.emptyValue
                              }
                          }
                          return UNDEFINED_VALUE
                      }
                      return titleCase(value)
                  },
        },
    })
}

export function createLabelLookupField<RecordType>(
    options: FieldCreatorSetupOptions<RecordType> & {
        labels: Record<string, string>
    }
): Field<RecordType> {
    return createField({
        minWidth: options.minWidth ?? 120,
        width: options.width ?? 150,
        ...options,
        renderOptions: {
            ...options.renderOptions,
            render: options.renderOptions?.render
                ? options.renderOptions.render
                : ({ value, record }) => (
                      <span>
                          {isUnset(value)
                              ? ((typeof options.emptyValue === 'function'
                                    ? options.emptyValue(record)
                                    : options.emptyValue) ?? UNDEFINED_VALUE)
                              : (options.labels[value] ??
                                options.emptyValue ??
                                UNDEFINED_VALUE)}
                      </span>
                  ),
        },
    })
}

type FieldType =
    | 'string'
    | 'percentage'
    | 'date'
    | 'integer'
    | 'float'
    | 'currency'
    | 'datetime'
    | 'timestamp'

export function createFieldOfType<RecordType>(
    fieldType: FieldType,
    options: FieldCreatorSetupOptions<RecordType>
): Field<RecordType> {
    switch (fieldType) {
        case 'currency':
            return createCurrencyField(options)
        case 'date':
            return createDateField(options)
        case 'datetime':
        case 'timestamp':
            return createCerebroDateTimeField(options)
        case 'string':
            return createField(options)
        case 'integer':
            return createField(options)
        case 'float':
            return createNumberField(options)
        case 'percentage':
            return createPercentageField(options)
        default: {
            const exhaustiveCheck: never = fieldType
            throw new Error(
                `Unhandled createFieldOfType case: ${exhaustiveCheck}`
            )
        }
    }
}
